import React, { lazy, Suspense, useCallback } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { createGlobalStyle } from 'styled-components';
import { Box } from '@mui/material';

import DashboardContent from '@core/dashboard/components/DashboardContent';
import DashboardBarButtons from '@core/dashboard/components/DashboardBarButtons';
import EditorMode from '@core/dashboard/components/EditorMode';
import BrandingTransition from '@core/dashboard/components/BrandingTransition';
import PoiPreview from '@core/dashboard/components/PoiPreview';
import LeaveRegionDialog from '@core/dashboard/components/LeaveRegionDialog';
import TeleportToRoomDialog from '@core/dashboard/components/TeleportToRoomDialog';
import AdminSettings from '@core/dashboard/components/AdminSettings';
import DolbyCameraDialog from '@core/dashboard/components/DolbyCameraDialog';
import OrientationOverlay from '@core/dashboard/components/Overlays/OrientationOverlay';
import { useShowOrientationOverlay } from '@core/dashboard/components/Overlays/OrientationOverlay/hooks';
import MapModal from '@core/dashboard/components/MapModal/MapModal';
import TeleportModal from '@core/dashboard/components/TeleportModal/TeleportModal';
import { useTeleportKeyboardShortcut } from '@core/dashboard/components/TeleportModal/hooks/useTeleportKeyboardShortcut';
import { useHiddenWhileEditing } from '@core/dashboard/hooks/useHiddenWhileEditing';
import { IEventPageView } from './interfaces';

import { useStyles } from './styles';
import { useEventContext } from '@providers/EventProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import MobileExternalKeyboardControls from '@components/VideoGameCast/MobileExternalKeyboardControls';
import { OrientationFromTo } from '@core/dashboard/components/Overlays/OrientationOverlay/interfaces';
import ExternalKeyboardOverlay from '@core/dashboard/components/Overlays/ExternalKeyboardOverlay';
import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import {
  TutorialProvider,
  TutorialPromptModal,
  TutorialRunningControls,
  TutorialStepText,
  TutorialCompletionNotice,
} from '@core/tutorials';

import { env } from 'env';

const ZoomScreenPreview = lazy(
  () => import('@features/zoom/components/ZoomScreenPreview'),
);

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #000000;
  }
`;

const EventPageView = ({ openAdminSettingsModal }: IEventPageView) => {
  const { showOverlay, isLandscape, handleHideOverlay } =
    useShowOrientationOverlay();
  const { isEventRun } = useEventContext();
  const { showExternalKeyboardOverlay, currentRoom } = useMytaverseEvent();
  const { currentScreen } = useMytaverse();

  // Hide the teleport menu while the branding editor's Inspector is open — it
  // occupies the same right-edge screen area (MV-1551). This only gates
  // rendering; the teleport zustand store is left untouched, so the modal
  // reappears in its prior open state when the Inspector closes.
  const isHiddenWhileEditing = useHiddenWhileEditing();

  const showOverlayWhenWorldIsLoaded =
    showOverlay && currentScreen === CurrentScreenEnum.WORLD;

  const classes = useStyles(
    showOverlayWhenWorldIsLoaded,
    isEventRun && !!currentRoom,
  );

  useTeleportKeyboardShortcut();

  const screenPreview = () => {
    switch (env.REACT_APP_FEATURE_THIRD_PARTY_COMMUNICATIONS_PROVIDER) {
      case 'ZOOM':
        return (
          <Suspense>
            <ZoomScreenPreview />
          </Suspense>
        );

      default:
        return (
          <>
            <video
              id="screen-share-content-video"
              style={classes.video(isDesktop)}
            ></video>
            <canvas
              id="screen-share-content-canvas"
              style={classes.video(isDesktop)}
            ></canvas>
          </>
        );
    }
  };

  const handleExternalKeyboardOverlayClick = useCallback(() => {
    if (isLandscape) {
      handleHideOverlay();
    }
  }, [isLandscape, handleHideOverlay]);

  return (
    <TutorialProvider>
      {screenPreview()}
      {/*<SpeedTestCheck />*/}
      <Box id="home" sx={classes.homePage}>
        <DashboardBarButtons />
        {/* Deliberately a sibling of the bars rather than a child of one. It used to hang off
            HomeBottomBar, which meant the map editor's persistence bridge — the thing that puts the
            saved layout back into Unreal on arrival — was gated on a stack of visibility rules that
            have nothing to do with it: the event running, the controls showing, `isHideWebUI`, and
            the bar's own `isHideWebUIExceptAssistant` / `isHideAllExceptTopBar` early returns. A
            venue that hides the web UI therefore had participants who never republished, so a
            restarted server stayed an empty world. The overlay it renders is fixed-position with its
            own z-index and HomeBottomBarProvider puts no element in the DOM, so this is the same
            position on the page it already occupied. */}
        <EditorMode />
        {/* A sibling of the editor rather than a child of it: a configuration can also be made live
            from outside editor mode, and this covers the re-skin either way. Its own z-index puts it
            over both. */}
        <BrandingTransition />
        <Box sx={classes.root}>
          <DashboardContent />
          <PoiPreview />
          <LeaveRegionDialog />
          <TeleportToRoomDialog />
          <DolbyCameraDialog />
          <MapModal />
          {!isHiddenWhileEditing && <TeleportModal />}
          {openAdminSettingsModal && <AdminSettings />}
          <TutorialPromptModal />
        </Box>
      </Box>
      {showOverlayWhenWorldIsLoaded && (
        <OrientationOverlay
          orientation={OrientationFromTo.PORTRAIT_TO_LANDSCAPE}
        />
      )}
      {showExternalKeyboardOverlay && (
        <ExternalKeyboardOverlay
          onReconnectClick={handleExternalKeyboardOverlayClick}
          onSkipClick={handleExternalKeyboardOverlayClick}
        />
      )}
      {!isDesktop ? <MobileExternalKeyboardControls /> : ''}

      {isEventRun && isMobile && isLandscape && !showOverlay && <GlobalStyle />}
      <TutorialRunningControls />
      <TutorialStepText />
      <TutorialCompletionNotice />
    </TutorialProvider>
  );
};

export default EventPageView;
